import { CommonModule } from "@angular/common";
import { Directive, EventEmitter, Output, ElementRef, NgModule, Renderer2, Input, AfterViewInit, OnDestroy, OnChanges, SimpleChanges } from "@angular/core";

enum NormalizeCharacters { WIDE = 'wide', SPACE = 'space', ACCENTS = 'accents' }
const emptyScore = {
    wide: 0,
    space: 0,
    accents: 0
};
@Directive({
    selector: '[w-normalize]',
    standalone: false
})
/*
@id w-normalize
@name w-normalize
@status release
@parents [w-input-text, w-input-textarea]
@description directive to normalize wide latin characters and punctuation to half-width characters, or 
any kind of unicode space to simple space. 
It is applicable to components wrapping HTMLInput or HTMLTextArea

*/
export class WNormalizeDirective implements AfterViewInit, OnDestroy, OnChanges {
    private inputElement: HTMLInputElement | HTMLTextAreaElement | null = null;
    @Input('w-normalize') enabled: boolean | string = false; // we can dynamically switch normalization on/off, when no value provided, it is equivalent to "true"
    /**
     * we can specify the named ranges to normalize, 'wide' - all wide Latin characters (from East Asian keyboard) and punctuation, "space" - all unicode spaces, "accents" - remove all accent marks
     */
    @Input() normalizeCharacters: string = 'wide,space,accents'; // @options [wide,space,accents] define groups of characters to normalize
    @Output() wNormalize = new EventEmitter<Record<string, number>>(true); // emits statistics of normalized values per range
    private score = { ...emptyScore };

    private listener = this.onInput.bind(this);
    private listening = false;

    // tslint:disable-next-line: variable-name
    constructor(private renderer: Renderer2, private elRef: ElementRef<HTMLElement>) { }
    ngOnChanges(changes: SimpleChanges): void {
        const enabled = changes['enabled'];
        if (enabled.previousValue !== enabled.currentValue) {
            this.listen(!!this.enabled || this.enabled === '');
        }
        // any input change will init score
        this.score = { ...emptyScore };
    }
    ngOnDestroy() {
        this.listen(false);
    }
    ngAfterViewInit(): void {
        // Find the first input element within the component's content.
        this.inputElement = this.elRef.nativeElement.querySelector('input') || this.elRef.nativeElement.querySelector('textarea');
        this.listen(!!this.enabled || this.enabled === '');
    }
    private listen(enable: boolean): void {
        if (this.inputElement) {
            if (this.listening && !enable) {
                this.inputElement?.removeEventListener('input', this.listener);
            } else if (!this.listening && enable) {
                this.inputElement.addEventListener('input', this.listener);
            }
            this.listening = enable;
        }
    }
    private onInput(event: Event): void {
        if (!this.inputElement) {
            return;
        }

        const originalValue = this.inputElement.value;
        let normalizedValue = originalValue;
        if (this.normalizeCharacters.indexOf(NormalizeCharacters.WIDE) > -1) {
            const v = this.normalizeWideChars(normalizedValue);
            this.score[NormalizeCharacters.WIDE] += countReplacedCharacters(normalizedValue, v);
            normalizedValue = v;
        }
        if (this.normalizeCharacters.indexOf(NormalizeCharacters.SPACE) > -1) {
            let v = this.normalizeSpaces(normalizedValue);
            this.score[NormalizeCharacters.SPACE] += countReplacedCharacters(normalizedValue, v);
            normalizedValue = v;
            v = this.removeZeroSpaces(normalizedValue);
            this.score[NormalizeCharacters.SPACE] += normalizedValue.length - v.length;
            normalizedValue = v;
            v = this.normalizeSeparators(normalizedValue);
            this.score[NormalizeCharacters.SPACE] += countReplacedCharacters(normalizedValue, v);
            normalizedValue = v;
        }
        if (this.normalizeCharacters.indexOf(NormalizeCharacters.ACCENTS) > -1) {
            const v = this.normalizeAccents(normalizedValue);
            this.score[NormalizeCharacters.ACCENTS] += countReplacedCharacters(normalizedValue, v);
            normalizedValue = v;
        }
        if (originalValue !== normalizedValue) {
            const start = Math.min(this.inputElement.selectionStart ?? 0, normalizedValue.length);
            const end = Math.min(this.inputElement.selectionEnd ?? 0, normalizedValue.length);
            this.renderer.setProperty(this.inputElement, 'value', normalizedValue);
            this.inputElement.setSelectionRange(start, end);

            this.wNormalize.emit(this.score);
            const event = new Event('input', { bubbles: true, cancelable: true });
            this.inputElement.dispatchEvent(event); // Dispatch the input event to notify Angular of the change
        }
    }
    private normalizeSpaces(text: string) {
        if (!text) {
            return text;
        }
        // Replace various Unicode spaces with standard ASCII space
        return text.replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000\u1680]/g, ' ');
    }
    private removeZeroSpaces(text: string) {
        if (!text) {
            return text;
        }
        // remove zero length spaces
        return text.replace(/[\u200B-\u200F\u202F\u180e\u2060\uFEFF]/g, '');
    }
    private normalizeSeparators(text: string) {
        if (!text) {
            return text;
        }
        // remove zero length spaces
        return text.replace(/[\u2028\u2029]/g, '\n');
    }
    private normalizeWideChars(text: string): string {
        if (!text) {
            return text;
        }
        return text
            .replace(/[\uff01-\uff5e]/g, (char) => {
                return String.fromCharCode(char.charCodeAt(0) - 0xFEE0);
            }).replace(/[\u3000]/g, ' ') // full-width space
    }
    private normalizeAccents(text: string) {
        if (!text) {
            return text;
        }
        // ONLY remove accent marks
        return text.normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .normalize('NFC'); // Re-compose for stability
    }
}
@NgModule({
    imports: [CommonModule],
    exports: [WNormalizeDirective],
    declarations: [WNormalizeDirective]
})
export class WNormalizeModule { }
/**
 * Counts the number of character replacements between two strings
 * @param initialString The original string before replacements
 * @param processedString The string after replacements
 * @returns The number of characters that differ (considering the longer string's extra chars as differences)
 */
const countReplacedCharacters = (initialString: string, processedString: string): number =>
    [...initialString].filter((char, index) => char !== (processedString[index])).length;